
import React from 'react';
import { 
  X, Plus, Trash2, Save, ShoppingCart, 
  Building, Calendar, FileText, User, CreditCard, 
  DollarSign, Settings, Search, Package, Calculator,
  Tag, Percent, Star, ArrowRight, CheckCircle2,
  Barcode, Hash, Clock, Info, Edit, ArrowDown
} from 'lucide-react';
import { Modal, FormInput, FormSelect, FormTextarea, Button, Badge, SearchableSelect } from '@/components/ui';
import { fmt } from '@/lib/formatters';

/**
 * PurchaseEntryModal Component
 * Refactored to follow a strict 2-field per row layout as requested.
 * Uses standard Venus Solar UI components for a premium, consistent look.
 */
export const PurchaseEntryModal = ({ 
  isOpen, 
  onClose, 
  form, 
  setForm, 
  onSubmit, 
  onAddToList, 
  onRemoveFromList, 
  onEditItem, 
  onSelectExistingItem,
  inventory = [], 
  dynamicSettings = {}, 
  masterItems = [],
  loading = false 
}) => {
  const { suppliers = [], cats = [], brands = [], units = [], stores = [] } = dynamicSettings;
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [expandedIdx, setExpandedIdx] = React.useState(null);
  const scrollRef = React.useRef(null);

  const updateField = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
  };

  const filteredItems = React.useMemo(() => {
    // Merge master items and current inventory for a comprehensive search
    const allProducts = [...(inventory || []), ...(masterItems || [])];
    
    // Create unique list by name to avoid duplicates
    const uniqueMap = new Map();
    allProducts.forEach(item => {
      const name = (item.itemName || item.name || '').trim();
      if (name && !uniqueMap.has(name)) {
        uniqueMap.set(name, item);
      }
    });
    
    const uniqueProducts = Array.from(uniqueMap.values());

    if (!searchTerm) return uniqueProducts;
    
    return uniqueProducts.filter(item =>
      (item.itemName || item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.itemCode || item.code || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [inventory, masterItems, searchTerm]);
  const onSelectItem = (item) => {
    onSelectExistingItem(item);
    setSearchTerm("");
    setShowDropdown(false);
  };

  if (!isOpen || !form) return null;

  const isExistingItem = !!form._id;
  const disabledClass = isExistingItem ? "cursor-not-allowed opacity-75 shadow-none" : "";

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      className="max-w-[830px]"
      title={
        <div className="flex items-center gap-2">
          <div>
            <h2 className="font-semibold text-lg text-brand-dark">Record New Purchase</h2>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full max-h-[85vh]">
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto pr-2 no-scrollbar pb-6 space-y-6"
        >
          {/* 01. Purchase Details */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>01. Purchase Details</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormSelect
                label="Vendor"
                value={form.supplier}
                onChange={(e) => updateField('supplier', e.target.value)}
                options={[
                  { value: "", label: "Select Vendor" },
                  ...suppliers.map(s => {
                    const val = s.vendorName || s.name || s.title || (typeof s === 'string' ? s : 'Unknown Vendor');
                    return { value: s._id || s.vendorName || val, label: val };
                  })
                ]}
                required={true}
              />
              <FormInput
                label="Invoice Number"
                value={form.invoiceNo}
                onChange={(e) => updateField('invoiceNo', e.target.value)}
                placeholder="e.g. INV-123"
                required={true}
              />
              <FormInput
                label="Invoice Date"
                type="date"
                value={form.invoiceDate}
                onChange={(e) => updateField('invoiceDate', e.target.value)}
                required={true}
              />
              <FormSelect
                label="Store"
                value={form.store}
                onChange={(e) => updateField('store', e.target.value)}
                options={[
                  { value: "", label: "Select Store" },
                  ...stores.map(st => {
                    const val = st.storeName || st.title || st.name || st.label || (typeof st === 'string' ? st : 'Unknown Store');
                    return {
                      value: st._id || val, 
                      label: val 
                    };
                  })
                ]}
                required={true}
              />
            </div>
          </div>

          {/* 02. Item Configuration */}
          <div className="space-y-6">
              <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>02. Item Configuration</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div className="relative">
                {/* Diagnostic Log */}
                {(() => {
                  if (showDropdown && isOpen) {
                    console.log("Purchase Search Debug:", {
                      searchTerm,
                      masterItemsCount: masterItems?.length,
                      inventoryCount: inventory?.length,
                      filteredCount: filteredItems?.length
                    });
                  }
                  return null;
                })()}

                <SearchableSelect
                  label="Item Name"
                  placeholder="Enter item name or search..."
                  value={form._id || form.name}
                  onChange={(val) => {
                    const selected = filteredItems.find(i => (i._id || i.id || i.name) === val);
                    if (selected) {
                      onSelectItem(selected);
                    } else if (typeof val === 'string') {
                      updateField('name', val);
                    }
                  }}
                  allowCustom={true}
                  options={filteredItems.map(item => ({
                    value: item._id || item.id || item.itemName || item.name,
                    label: item.itemName || item.name || 'Unnamed Item',
                    dropdownLabel: (
                      <div className="flex flex-col gap-1 w-full text-left">
                        <div className="text-[11px] font-black text-slate-800 uppercase group-hover:text-brand-blue transition-colors">
                          {item.itemName || item.name || 'Unnamed Item'}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[9px] font-black text-brand-blue bg-blue-50 px-1.5 py-0.5 rounded tracking-widest uppercase">{item.itemCode || item.code || 'MASTER'}</span>
                          <span className="text-[9px] text-slate-400 font-bold uppercase">
                            {typeof item.category === 'object' ? (item.category?.categoryName || item.category?.title) : item.category}
                          </span>
                        </div>
                      </div>
                    )
                  }))}
                  required={true}
                  className="w-full relative z-[9999]"
                />
              </div>

              <FormSelect
                label="Item Type"
                value={form.itemType}
                onChange={(e) => updateField('itemType', e.target.value)}
                options={[
                  { value: "", label: "Select Item Type" },
                  { value: 'Product', label: 'Product' }, 
                  { value: 'Material', label: 'Material' }, 
                  { value: 'Service', label: 'Service' }
                ]}
                required={true}
                disabled={isExistingItem}
              />


              <FormSelect
                label="Category"
                value={form.category}
                onChange={(e) => updateField('category', e.target.value)}
                options={[
                  { value: "", label: "Select Category" },
                  ...cats.map(c => ({
                    value: c._id || c.title || c.name || c.categoryName || (typeof c === 'string' ? c : 'Unknown'),
                    label: c.title || c.name || c.categoryName || (typeof c === 'string' ? c : 'Unknown')
                  }))
                ]}
                disabled={isExistingItem}
         
              />

              <FormSelect
                label="Brand"
                value={form.brand}
                onChange={(e) => updateField('brand', e.target.value)}
                options={[
                  { value: "", label: "Select Brand" },
                  ...brands.map(b => ({
                    value: b._id || b.title || b.name || b.brandName || (typeof b === 'string' ? b : 'Unknown'),
                    label: b.title || b.name || b.brandName || (typeof b === 'string' ? b : 'Unknown')
                  }))
                ]}
                disabled={isExistingItem}
             
              />

              <FormInput
                label="Item Code / SKU"
                value={form.itemCode}
                onChange={(e) => updateField('itemCode', e.target.value)}
                placeholder="Item ID"
                readOnly={isExistingItem}
                inputClassName={isExistingItem ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}
              />

              <FormInput
                label="HSN / SAC Code"
                value={form.hsnCode}
                onChange={(e) => updateField('hsnCode', e.target.value)}
                placeholder="8-digit HSN"
                readOnly={isExistingItem}
                inputClassName={isExistingItem ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}
           
              />


              <div className={`flex flex-col justify-center space-y-4 ${isExistingItem ? 'opacity-70 pointer-events-none' : ''}`}>
                <label className={`flex items-center gap-3 cursor-pointer group ${isExistingItem ? 'cursor-not-allowed' : ''}`}>
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${form.isUnique ? 'bg-brand-blue border-brand-blue' : 'bg-white border-slate-300 group-hover:border-slate-400'}`}>
                    <input type="checkbox" className="hidden" checked={form.isUnique} onChange={(e) => updateField('isUnique', e.target.checked)} disabled={isExistingItem} />
                    {form.isUnique && <Plus size={12} className="text-white rotate-45" />}
                  </div>
                  <span className="text-[11px] font-black text-slate-500 uppercase tracking-tight group-hover:text-slate-800">Is Unique Item (Serial Managed)</span>
                </label>
                <label className={`flex items-center gap-3 cursor-pointer group ${isExistingItem ? 'cursor-not-allowed' : ''}`}>
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${form.hasBatchExpiry ? 'bg-brand-blue border-brand-blue' : 'bg-white border-slate-300 group-hover:border-slate-400'}`}>
                    <input type="checkbox" className="hidden" checked={form.hasBatchExpiry} onChange={(e) => updateField('hasBatchExpiry', e.target.checked)} disabled={isExistingItem} />
                    {form.hasBatchExpiry && <Plus size={12} className="text-white rotate-45" />}
                  </div>
                  <span className="text-[11px] font-black text-slate-500 uppercase tracking-tight group-hover:text-slate-800">Has Batch / Expiry Control</span>
                </label>
              </div>

              <FormInput
                label="Unique No Title (e.g. IMEI)"
                value={form.uniqueName}
                onChange={(e) => updateField('uniqueName', e.target.value)}
                placeholder={form.isUnique ? "e.g. Serial #" : "Check Is Unique first..."}
                disabled={!form.isUnique || isExistingItem}
                inputClassName={isExistingItem ? disabledClass : ""}
              />

              <FormInput
                label="Expiry Date"
                type="date"
                value={form.expiryDate}
                onChange={(e) => updateField('expiryDate', e.target.value)}
                disabled={!form.hasBatchExpiry}
              />

              <FormInput
                label="Barcode"
                value={form.barcode}
                onChange={(e) => updateField('barcode', e.target.value)}
                placeholder="Scan or enter barcode"
                icon={<Barcode size={14} className="text-slate-300" />}
                disabled={isExistingItem}
                inputClassName={disabledClass}
              />

              <FormSelect
                label="Units"
                value={form.unit}
                onChange={(e) => updateField('unit', e.target.value)}
                options={[
                  { value: "", label: "Select Unit" },
                  ...units.map(u => ({
                    value: u._id || u.title || u.name || u.unitName || (typeof u === 'string' ? u : 'Unknown'),
                    label: u.title || u.name || u.unitName || (typeof u === 'string' ? u : 'Unknown')
                  }))
                ]}
                disabled={isExistingItem}
              />
             

              <div className="sm:col-span-2">
                <FormTextarea
                  label="Description"
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Enter detailed description of the item..."
                  rows={2}
                  disabled={isExistingItem}
                  className={disabledClass}
                />
              </div>
            </div>
          </div>

          {/* 03. Billing Details */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>03. Billing Details</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormInput
                label="Purchase Amount/Rate (?)"
                type="number"
                value={form.rate}
                onChange={(e) => updateField('rate', e.target.value)}
                placeholder="0.00"
                required={true}
              />
              <FormInput
                label="Quantity"
                type="number"
                value={form.quantity}
                onChange={(e) => updateField('quantity', e.target.value)}
                placeholder="1"
                required={true}
              />
              <FormInput
                label="BV Points"
                type="number"
                value={form.bvPoints}
                onChange={(e) => updateField('bvPoints', e.target.value)}
                placeholder="0"
              />
              <FormInput
                label="Bottom Line Stock Quantity"
                type="number"
                value={form.availableQty}
                onChange={(e) => updateField('availableQty', e.target.value)}
                placeholder="0"
              />
                <span className="text-[11px] font-black text-slate-400 uppercase">Base Amount (Rate × Qty)</span>
              <div className="sm:col-span-2 bg-slate-50 rounded-lg p-2 border border-slate-100 flex items-center">
                <span className="">₹{fmt(form.baseAmount)}</span>
              </div>
            </div>
          </div>

          {/* 04. Discount Configuration */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>04. Discount Configuration</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormInput
                label="Discount (%)"
                type="number"
                value={form.discP}
                onChange={(e) => updateField('discP', e.target.value)}
                placeholder="0"
              />
              <FormInput
                label="Discount Amount (-)"
                type="number"
                value={form.discount}
                onChange={(e) => updateField('discount', e.target.value)}
                placeholder="0"
              />
            </div>
          </div>

          {/* 05. Tax Details */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>05. Tax Details</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormSelect
                label="Tax Percentage (%)"
                value={form.taxP}
                onChange={(e) => updateField('taxP', Number(e.target.value))}
                options={[{ value: 0, label: '0%' }, { value: 5, label: '5%' }, { value: 12, label: '12%' }, { value: 18, label: '18%' }, { value: 28, label: '28%' }]}
              />
            
              <FormInput label="IGST Amount (+)" type="number" value={form.igst} onChange={(e) => updateField('igst', e.target.value)} />
              <FormInput label="CGST Amount (+)" type="number" value={form.cgst} onChange={(e) => updateField('cgst', e.target.value)} />
              <FormInput label="SGST Amount (+)" type="number" value={form.sgst} onChange={(e) => updateField('sgst', e.target.value)} />
          
            </div>
          </div>

          {/* 06. Other Charges & Net Bill */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>06. Other Charges & Net Bill</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormInput 
                label="Net Bill (Taxable + Tax)" 
                type="number" 
                value={form.netBill} 
                onChange={(e) => updateField('netBill', e.target.value)} 
                readOnly={true}
                inputClassName="bg-slate-50 text-slate-500 cursor-not-allowed"
              />
              <FormInput label="Service Charges (+)" type="number" value={form.chargeAmount} onChange={(e) => updateField('chargeAmount', e.target.value)} />
              <FormInput label="Adjustments (+/-)" type="number" value={form.adjustments} onChange={(e) => updateField('adjustments', e.target.value)} />
            </div>

            <Button 
              type="button" 
              variant="primary" 
              fullWidth 
              onClick={() => {
                // Validate invoice details before adding item to list
                if (!form.supplier || !form.store || !form.invoiceNo) {
                  alert("Please fill in Vendor, Store, and Invoice Number before adding items to the invoice.");
                  return;
                }
                onAddToList();
                setExpandedIdx(null);
                scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="py-5 font-black text-[12px] uppercase tracking-[0.3em] shadow-xl shadow-blue-100 rounded-[1.25rem]"
              leftIcon={<Plus size={18} />}
            >
              Add Item to Invoice
            </Button>
          </div>

          {/* Invoice Items List Container */}
          {form.items && form.items.length > 0 && (
            <div className="space-y-5 pt-6">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                   <div className="w-1.5 h-1.5 rounded-full bg-brand-blue" />
                   Items in Invoice ({form.items.length})
                </h4>
                <div className="text-[10px] font-black text-brand-blue bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
                   Subtotal: ₹{fmt(form.items.reduce((s, i) => s + (Number(i.netBill || i.rowTotal) || 0), 0))}
                </div>
              </div>
              <div className="space-y-4">
                {form.items.map((item, idx) => {
                  const isExpanded = expandedIdx === idx;
                  return (
                    <div key={item.id || idx} className={`group rounded-[2rem] border transition-all duration-500 overflow-hidden ${isExpanded ? 'border-brand-blue bg-blue-50/20 ring-4 ring-brand-blue/5' : 'border-slate-100 bg-white hover:border-slate-300 shadow-sm'}`}>
                      <div className="p-6 flex items-center justify-between cursor-pointer" onClick={() => setExpandedIdx(isExpanded ? null : idx)}>
                        <div className="flex items-center gap-5">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black transition-all ${isExpanded ? 'bg-brand-blue text-white shadow-xl rotate-12' : 'bg-slate-100 text-slate-400'}`}>{idx + 1}</div>
                          <div>
                            <div className="text-sm font-black text-slate-800 uppercase tracking-tight">{item.name}</div>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.itemCode || 'MASTER'}</span>
                              <div className="w-1 h-1 rounded-full bg-slate-300" />
                              <span className="text-[10px] font-black text-brand-blue uppercase">{item.quantity} {(() => {
                                const u = units.find(x => x._id === item.unit);
                                return u ? (u.title || u.name || u.unitName || item.unit) : item.unit;
                              })()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-8">
                           <div className="text-right">
                              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 opacity-60">Total</div>
                              <div className="text-xl font-black text-slate-800 tracking-tighter">₹{fmt(item.netBill || item.rowTotal)}</div>
                           </div>
                           <div className={`transition-all duration-500 ${isExpanded ? 'rotate-180 text-brand-blue scale-125' : 'text-slate-300'}`}>
                             <ArrowDown size={20} />
                           </div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-8 pb-8 pt-4 border-t border-brand-blue/10 animate-in slide-in-from-top-4 duration-500">
                           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                              <div className="space-y-1.5">
                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Unit Details</div>
                                <div className="text-xs font-black text-slate-700 italic">₹{fmt(item.rate)} / {(() => {
                                  const u = units.find(x => x._id === item.unit);
                                  return u ? (u.title || u.name || u.unitName || item.unit) : item.unit;
                                })()}</div>
                              </div>
                              <div className="space-y-1.5">
                                <div className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">Discounts</div>
                                <div className="text-xs font-black text-blue-600">{item.discP}% off (₹{fmt(item.discount)})</div>
                              </div>
                              <div className="space-y-1.5">
                                <div className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">Tax Breakdown</div>
                                <div className="text-xs font-black text-blue-600">{item.taxP}% GST (₹{fmt(item.taxAmount)})</div>
                              </div>
                              <div className="flex items-center justify-end gap-3">
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEditItem(item.id); setExpandedIdx(null); scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }} className="w-10 h-10 rounded-xl text-blue-500 bg-blue-50 hover:bg-blue-500 hover:text-white transition-all"><Edit size={16} /></Button>
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onRemoveFromList(item.id); }} className="w-10 h-10 rounded-xl text-rose-500 bg-rose-50 hover:bg-rose-500 hover:text-white transition-all"><Trash2 size={16} /></Button>
                              </div>
                           </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Final Summary Highlight */}
          <div className="pt-8">
            <div className="p-6 rounded-lg bg-brand-blue/5 border border-brand-blue/10 flex flex-col md:flex-row md:items-center justify-between gap-10 group">
               <div className="space-y-2 relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse" />
                    <div className="text-[10px] font-black text-brand-blue uppercase tracking-[0.4em]">Final Net Payment</div>
                  </div>
                  <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">Inclusive of all taxes and adjustments</p>
               </div>
               <div className="text-4xl font-black text-brand-blue tracking-tighter relative z-10">
                  {fmt(form.items.reduce((s, i) => s + (Number(i.netBill || i.rowTotal) || 0), 0) + (Number(form.adjustments) || 0))}
               </div>
            </div>
          </div>
        </div>

        {/* Primary Action Buttons */}
        <div className="flex gap-4 pt-8 mt-4 border-t border-slate-100 bg-white px-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading} className="py-5 font-black text-[12px] uppercase tracking-[0.3em] rounded-[1.25rem]">Discard</Button>
          <Button 
            variant="primary" 
            fullWidth 
            onClick={() => onSubmit(form)} 
            loading={loading}
            className="py-5 font-black text-[12px] uppercase tracking-[0.3em] shadow-2xl shadow-blue-100 rounded-[1.25rem]"
            leftIcon={<CheckCircle2 size={18} />}
          >
            Complete Purchase Entry
          </Button>
        </div>
      </div>
    </Modal>
  );
};

